import { useMutation } from '@tanstack/react-query'
import { httpClient } from '../../shared/api/httpClient'
import { useAstreaEnvironment } from '../../shared/hooks/useAstreaEnvironment'

/**
 * Port of `app/clipping/clippingsListService.js` (AngularJS
 * `ClippingsListService`).
 *
 * Legacy service:
 *
 *   const BASE_URL = AuthService.getUrlAstreaPath() + '/api/v2/clippings/status'
 *   clippingsListService.getClippings = (email, clippingCodes) =>
 *     $http.get(BASE_URL, {params: {email, clippingCodes}})
 *
 * `AuthService.getUrlAstreaPath()` resolves to the base URL of the Astrea
 * backend *itself* (not `server_app` — see `app/register/authService.js`
 * and its React port, `shared/context/astreaEnvironments.js`), based on the
 * `urlAstrea` value stored on the logged-in user's session. This is the same
 * pattern already used by other legacy services that call out to Astrea
 * directly (`historiesService.js`, `actionAuditService.js`, ...), none of
 * which have a `server_app` proxy route — confirmed via
 * `grep -rn "clipping" server_app/` returning no matches. So this hook calls
 * `httpClient` with an absolute URL (`getUrlAstreaPath(urlAstrea) + '/api/v2/clippings/status'`)
 * instead of `server_app`'s relative `baseURL`, exactly mirroring the legacy
 * behavior. `httpClient`'s interceptors (Basic Auth + session header) still
 * apply, since they're registered on the same axios instance regardless of
 * the target host.
 *
 * Modeled as a `useMutation` rather than a `useQuery`: the legacy screen has
 * no data to fetch on mount (`ng-init="init()"` calls a function that isn't
 * even defined in the controller — dead markup, confirmed by reading
 * `clippingsListController.js` in full), it only fetches on-demand when the
 * user presses "Buscar" (`getClippings()`), with a fresh set of filters each
 * time. That is exactly `useMutation`'s shape (imperative trigger + its own
 * pending/error state) rather than `useQuery`'s (declarative key-driven
 * fetch-on-mount/param-change).
 */
export function useClippingsQuery() {
  const { urlAstreaPath } = useAstreaEnvironment()

  const mutation = useMutation({
    mutationFn: async ({ email, clippingCodes }) => {
      const url = `${urlAstreaPath}/api/v2/clippings/status`
      const response = await httpClient.get(url, {
        params: { email, clippingCodes },
        paramsSerializer: { indexes: null },
      })
      return response.data
    },
  })

  return {
    getClippings: mutation.mutateAsync,
    clippings: mutation.data,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export default useClippingsQuery
